import { useRecords } from "../../../../api/useRecords";
import { constructEegDataFilter } from "./calculationsUtils";
import { eegDataFields } from "./uiUtils";
import { EegDataRecord, EEGRecording } from "../types";
import { SelectedFilterState } from "../types";
import { useMemo } from "react";

type UseAllEegDataProps = {
    firstEEG: EEGRecording;
    secondEEG?: EEGRecording;
    selectedFilter: SelectedFilterState;
};

// New hook that fetches all EEG data at once
export const useAllEegData = ({ firstEEG, secondEEG, selectedFilter }: UseAllEegDataProps) => {
    // Create filters without metricId to get all metrics data
    const currentEegDataFilter = constructEegDataFilter(Number(firstEEG?.id), selectedFilter);
    const comparisonEegDataFilter = secondEEG && constructEegDataFilter(Number(secondEEG?.id), selectedFilter);

    const { data: firstEEGData, status: firstEEGDataStatus } = useRecords("eeg_data", eegDataFields, {
        extraOptions: {
            filter: {
                _and: currentEegDataFilter,
            },
            pageSize: -1,
        },
        enabled: firstEEG?.id ? true : false,
        fetch: {
            fetchOnFocus: false,
        },
        staleTime: 5 * 60 * 1000, // 5 minutes cache
    });

    const {
        data: secondEEGData,
        status: secondEEGDataStatus,
        isFetching: isFetchingSecondEEGData,
    } = useRecords("eeg_data", eegDataFields, {
        extraOptions: {
            filter: {
                _and: comparisonEegDataFilter,
            },
            pageSize: -1,
        },
        enabled: secondEEG ? true : false,
        fetch: {
            fetchOnFocus: false,
        },
        staleTime: 5 * 60 * 1000, // 5 minutes cache
    });

    return {
        firstEEGData: firstEEGData?.items as Array<EegDataRecord>,
        secondEEGData: secondEEGData?.items as Array<EegDataRecord>,
        firstEEGDataStatus,
        secondEEGDataStatus,
        isFetchingSecondEEGData,
    };
};

// Hook that filters the aggregated data for a specific metric
export const useEegDataForMetric = (metricId: number, allEegData: ReturnType<typeof useAllEegData>) => {
    const filteredFirstEEGData = useMemo(() => {
        const firstEEGData = allEegData.firstEEGData;
        if (!firstEEGData) return [];
        return firstEEGData.filter((record) => record.metric.id === metricId);
    }, [allEegData.firstEEGData, metricId]);

    const filteredSecondEEGData = useMemo(() => {
        const secondEEGData = allEegData.secondEEGData;
        if (!secondEEGData) return [];
        return secondEEGData.filter((record) => record.metric.id === metricId);
    }, [allEegData.secondEEGData, metricId]);

    return {
        firstEEGData: filteredFirstEEGData,
        secondEEGData: filteredSecondEEGData,
        firstEEGDataStatus: allEegData.firstEEGDataStatus,
        secondEEGDataStatus: allEegData.secondEEGDataStatus,
        isFetchingSecondEEGData: allEegData.isFetchingSecondEEGData,
    };
};
